/**
 * Who lands in the field workspace. People whose role is field work (installers, installer leaders,
 * subcontractors: `field.use` without office capabilities) start on My work instead of the home page,
 * and /field (Measurements) sends them there when they don't measure. The API enforces access; this
 * only routes.
 */
import type { ReactNode } from "react";
import { Navigate, useLocation, useParams } from "react-router";

import { useTenant } from "@/hooks/useTenant";

export const FIELD_USE = "field.use";
export const MEASURES = ["measurements.schedule", "measurements.record", "measurements.verify"];

/** Capabilities that make someone an office (or shop) user, whose home stays the home page. */
const OFFICE = [
  "dashboard.view_dashboard",
  "tenant.manage_settings",
  "work_orders.view_all",
  "work_orders.manage",
  "work_orders.assign",
  "work_orders.approve",
  "schedule.manage",
  "estimates.view_estimate",
  "sales_orders.view_order",
  "purchasing.view_orders",
  "receiving.receive",
  "inventory.view_stock",
  "billing.view",
  ...MEASURES,
];

export function isFieldFirst(can: (code: string) => boolean): boolean {
  return can(FIELD_USE) && !OFFICE.some((code) => can(code));
}

/** /field: Measurements for people who measure, My work for field people who don't. */
export function FieldIndex({ children }: { children: ReactNode }) {
  const { can } = useTenant();
  if (can(FIELD_USE) && !MEASURES.some((code) => can(code))) return <Navigate to="work" replace />;
  return <>{children}</>;
}

/**
 * jobs/work-orders/:workOrderId for field-first people (installers following a notification or a
 * shared link): their screen for a work order is field/work/:workOrderId. Office users see children.
 */
export function FieldFirstWorkOrder({ children }: { children: ReactNode }) {
  const { slug, can } = useTenant();
  const { workOrderId } = useParams();
  const { hash } = useLocation();
  if (workOrderId && isFieldFirst(can)) return <Navigate to={`/t/${slug}/field/work/${workOrderId}${hash}`} replace />;
  return <>{children}</>;
}
